<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="flex justify-between items-center">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-200">Queue Dashboard</h2>
        <p class="text-gray-600 dark:text-gray-400">Monitor your BullMQ queues and jobs</p>
      </div>

      <div class="flex items-center space-x-3">
        <!-- Unpin All Button (only show if there are pinned queues) -->
        <button
          v-if="pinnedQueues.size > 0"
          @click="unpinAllQueues"
          class="btn-secondary flex items-center text-sm"
        >
          <XMarkIcon class="w-4 h-4 mr-2" />
          Unpin All ({{ pinnedQueues.size }})
        </button>

        <!-- Refresh Button -->
        <button
          @click="refreshQueues"
          :disabled="loading"
          class="btn-secondary flex items-center"
        >
          <ArrowPathIcon class="w-4 h-4 mr-2" :class="{ 'animate-spin': loading }" />
          Refresh
        </button>
      </div>
    </div>

    <!-- Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        class="border-gray-200 dark:border-gray-800 border"
        title="Total Queues"
        :value="filteredQueues.length"
        icon="QueueListIcon"
        color="primary"
      />
      <StatCard
        class="border-gray-200 dark:border-gray-800 border"
        title="Total Jobs"
        :value="totalJobs"
        icon="BriefcaseIcon"
        color="primary"
      />
      <StatCard
        class="border-gray-200 dark:border-gray-800 border"
        title="Active Jobs"
        :value="queuesByState.active || 0"
        icon="PlayIcon"
        color="success"
      />
      <StatCard
        class="border border-gray-200 dark:border-gray-800"
        title="Failed Jobs"
        :value="queuesByState.failed || 0"
        icon="ExclamationTriangleIcon"
        color="danger"
      />
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-danger-50 dark:bg-danger-900 border border-danger-200 rounded-lg p-4">
      <div class="flex">
        <ExclamationTriangleIcon class="h-5 w-5 text-danger-400 dark:text-danger-200" />
        <div class="ml-3">
          <h3 class="text-sm font-medium text-danger-800 dark:text-danger-200">Error loading queues</h3>
          <p class="mt-1 text-sm text-danger-700 dark:text-danger-50">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Queues List -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Queues</h3>
          
          <div class="flex items-center space-x-3">
            <!-- Search Input -->
            <div class="relative max-w-xs">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-5 w-5 text-gray-400 dark:text-gray-100" />
              </div>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search queues..."
                class="input-field"
              />
              <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  @click="clearSearch"
                  class="text-gray-400 hover:text-gray-600 dark:text-gray-100 hover:dark:text-gray-300"
                >
                  <XMarkIcon class="h-4 w-4" />
                </button>
              </div>
            </div>

            <!-- Sort Dropdown -->
            <div class="relative">
              <select
                v-model="sortOption"
                @change="setSortOption"
                class="input-field pr-8 min-w-[160px] text-gray-400 dark:text-gray-100"
              >
                <option value="alphabetical">Alphabetically</option>
                <option value="waiting">By Waiting Jobs</option>
                <option value="active">By Active Jobs</option>
                <option value="completed">By Completed Jobs</option>
                <option value="failed">By Failed Jobs</option>
                <option value="delayed">By Delayed Jobs</option>
                <option value="paused">By Paused Jobs</option>
                <option value="prioritized">By Prioritized Jobs</option>
                <option value="waiting-children">By Waiting Children</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && queues.length === 0" class="p-6">
        <div class="animate-pulse space-y-4">
          <div v-for="i in 3" :key="i" class="h-16 bg-gray-200 dark:bg-gray-800 rounded"></div>
        </div>
      </div>

      <div v-else-if="sortedQueues.length === 0 && searchQuery" class="p-6 text-center text-gray-500 dark:text-gray-100">
        <div class="space-y-2">
          <MagnifyingGlassIcon class="h-8 w-8 text-gray-300 dark:text-gray-100 mx-auto" />
          <p>No queues found matching "{{ searchQuery }}"</p>
          <button
            @click="clearSearch"
            class="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
          >
            Clear search
          </button>
        </div>
      </div>

      <div v-else-if="queues.length === 0" class="p-6 text-center text-gray-500 dark:text-gray-100">
        No queues found
      </div>

      <div v-else class="divide-y divide-x-0 divide-gray-200 rounded-b-lg overflow-hidden">
        <QueueCard
          v-for="queue in sortedQueues"
          :key="queue.name"
          :queue="queue"
          @click="state => navigateToQueue(queue.name, state)"
        />
      </div>
    </div>

    <!-- Last Updated -->
    <div v-if="lastUpdated" class="text-sm text-gray-500 text-center">
      Last updated: {{ formatTimestamp(lastUpdated) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useQueuesStore } from '@/stores/queues'
import { useSettingsStore } from '@/stores/settings'
import { ArrowPathIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import StatCard from '@/components/StatCard.vue'
import QueueCard from '@/components/QueueCard.vue'
import { formatTimestamp } from '@/utils/date'

const router = useRouter()
const queuesStore = useQueuesStore()
const settingsStore = useSettingsStore()

const {
  queues,
  loading,
  error,
  lastUpdated,
  totalJobs,
  queuesByState,
  filteredQueues,
  sortedQueues,
  searchQuery,
  sortOption,
  pinnedQueues,
} = storeToRefs(queuesStore)

const { settings, autoRefreshEnabled } = storeToRefs(settingsStore)

let refreshInterval: ReturnType<typeof setInterval> | null = null

function refreshQueues() {
  queuesStore.fetchQueues()
}

function navigateToQueue(queueName: string, state: string = '') {
  router.push({ name: 'queue-detail', params: { name: queueName }, query: { state: state || 'waiting' } })
}

function clearSearch() {
  queuesStore.clearSearch()
}

function setSortOption() {
  // The v-model will automatically update the sortOption value
  // No additional action needed as the computed sortedQueues will react to the change
}

function unpinAllQueues() {
  queuesStore.unpinAllQueues()
}

function setupAutoRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }

  if (autoRefreshEnabled.value && settings.value.autoRefreshInterval > 0) {
    refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshQueues()
      }
    }, settings.value.autoRefreshInterval * 1000)
  }
}

onMounted(() => {
  refreshQueues()
  setupAutoRefresh()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})

// Watch for settings changes
import { watch } from 'vue'
watch([autoRefreshEnabled, () => settings.value.autoRefreshInterval], setupAutoRefresh)
</script>
